import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { DocsView } from './features/docs/DocsView';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './auth/RequireAuth';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { AppShell } from './components/layout/AppShell';
import { DEFAULT_VIEW_PATH } from './components/layout/views';
import AdminView from './features/admin';
import BacklogView from './features/backlog';
import BoardView from './features/board';
import ListView from './features/list';
import RecurrenceView from './features/recurrence';
import SettingsView from './features/settings';
import SpacesView from './features/spaces';
import VersionsView from './features/versions';
import { LoginPage } from './pages/LoginPage';
import { ThemeProvider } from './theme/ThemeProvider';

/**
 * 🔴 THE ROUTE CONTRACT. Six views, two authenticated non-tabs, and login. This file is the ONLY
 * place they are wired.
 *
 *   /login      · no shell — a login screen with a project sidebar would be a lie
 *   /backlog    · AppShell → default export of src/features/backlog/index.{ts,tsx}
 *   /list       · AppShell → default export of src/features/list/index.{ts,tsx}
 *   /board      · AppShell → default export of src/features/board/index.{ts,tsx}
 *   /versions   · AppShell → default export of src/features/versions/index.{ts,tsx}
 *   /spaces     · AppShell → default export of src/features/spaces/index.{ts,tsx}
 *   /recurrence · AppShell → default export of src/features/recurrence/index.{ts,tsx}
 *   /admin      · AppShell, NOT a tab — gate inside the view
 *   /settings   · AppShell, NOT a tab — gate inside the view
 *   *           · redirect to /backlog, KEEPING the query string
 *
 * 🔴🔴 THREE OF THESE WERE MISSING UNTIL 2026-08-17 (PH-26). `/settings`, `/spaces` and `/recurrence`
 * were finished folders with passing `.dom.test.tsx` suites and no route at all, so all three fell
 * through to the `*` redirect and landed on `/backlog`. ⭐⭐ AND THE SUITES STAYED GREEN: a page test
 * mounts the page directly, which is exactly the assertion that cannot notice an unrouted page.
 * `App.routes.dom.test.tsx` is the detector that can — it renders THIS table and nothing else.
 *
 * ⛔ ONE index per feature folder. `index.ts` and `index.tsx` can both exist and only the `.ts` is
 * ever loaded — a second entry file does not error, it silently loses, and the view you edited is
 * not the view on screen.
 *
 * ⭐ Each view is imported as the DEFAULT export of a folder index. That is the whole point: the
 * three views still being built already have routes, a placeholder and a name, so finishing one is
 * an edit to one file in one folder and never a change to this one. ⛔ Deep-importing a component
 * out of a feature folder from here would put this file back in everybody's way.
 *
 * ⭐ The shell is a LAYOUT ROUTE, so the top bar, project rail and bottom tab bar mount once and
 * survive a view change. Re-mounting them per route would re-fetch `/projects` on every tab and
 * flash the chrome — the thing that is supposed to be the fixed point of the screen.
 */
export function App() {
  return (
    <ThemeProvider>
      <AppErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </AppErrorBoundary>
    </ThemeProvider>
  );
}

/**
 * ⭐ THE TABLE ON ITS OWN, EXPORTED, so a test can mount it under a `MemoryRouter` at any path.
 * `App` hard-wires `BrowserRouter`, which cannot be started at `/spaces`; without this split the
 * only way to check "does this path render its page or redirect?" is a real browser, which is why
 * nobody checked for two weeks.
 * ⛔ It is a component, not a data structure. A `ROUTES` array that both this and a test rendered
 * would let the test pass against an array the router never reads.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/backlog" element={<BacklogView />} />
        <Route path="/list" element={<ListView />} />
        <Route path="/board" element={<BoardView />} />
        <Route path="/versions" element={<VersionsView />} />
        <Route path="/spaces" element={<SpacesView />} />
        <Route path="/recurrence" element={<RecurrenceView />} />
        {/**
         * ⛔ THE TWO NON-TABS. Both are INSIDE `RequireAuth` and both are absent from `VIEWS`, which
         * drives the desktop tab row AND the 390px bottom bar — surfaces every signed-in user sees.
         *
         * ⭐ Each gates ITSELF, inside the view, rather than being wrapped in a redirecting guard:
         *   · `/admin`    → `RequireAdmin` renders "Administrators only".
         *   · `/settings` → readable by anyone (the GETs are open); the WRITE half is disabled with
         *                   an explanation, because the API's write routes are `requireAdmin`.
         * A non-admin who types either URL is told why, which a redirect cannot do — a redirect says
         * "no such page", and that is a different and false claim.
         */}
        {/* ⭐ Documents. Inside RequireAuth like every other view — a raw API link is not, which
            is why it failed on mobile. Absent from VIEWS deliberately: reached by link, not a tab. */}
        <Route path="/docs" element={<DocsView />} />
        <Route path="/admin" element={<AdminView />} />
        <Route path="/settings" element={<SettingsView />} />
      </Route>
      <Route path="*" element={<RedirectToDefaultView />} />
    </Routes>
  );
}

/**
 * ⭐ Carries the query string across the redirect. `/?project=4` is a link somebody sends, and a bare
 * `<Navigate to="/backlog">` would drop the project on the floor and land them on everything —
 * which looks like the link worked and quietly showed them the wrong thing.
 */
function RedirectToDefaultView() {
  const location = useLocation();
  return <Navigate to={{ pathname: DEFAULT_VIEW_PATH, search: location.search }} replace />;
}
